import React, { useEffect, useMemo, useState, useRef, lazy, Component } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck, Calculator as CalcIcon, X, Plus, Minus } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems, getDefaultQuantity, getSmallLoadSurcharge } from '../utils/loadSplit';
import { CartHelper } from '../components/CartHelper';
import { SimpleAreaCalculator } from '../components/SimpleAreaCalculator';
import { ProductComparisonPanel } from '../components/ProductComparisonPanel';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2833" data-mp="1ovhm22r3owyg">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-[#e8ede4] dark:bg-emerald-500/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5fb478] focus:border-transparent transition-all flex items-center justify-between font-medium hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] ${value ? '' : 'text-stone-500'}`} data-id="element-2834" data-mp="1jxg27fluotlx">
        <span className="truncate" data-id="element-2835" data-mp="39euvc123glf0">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2836" data-mp="wowv9208zkw" />
      </button>

      <AnimatePresence data-id="element-2837" data-mp="3o3rasj6hd0pn">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full top-full mt-2 max-h-60 overflow-y-auto overflow-x-hidden border border-[#d4ddcf] bg-white pb-2.5 shadow-xl dark:border-white/10 dark:bg-stone-900" data-id="element-2838" data-mp="3o2pnngc6wnrk">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#e3f7e8] dark:bg-[#0f3e1e] text-[#1a7a3a] dark:text-[#a8e8b8] font-semibold' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-2839" data-mp="298hoqp0qlcoq">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-2840" data-mp="12oi3a6j2c4mv" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-2841" data-mp="5x4l23kjh7nz">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-2842" data-mp="a1ccw9man3qf" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2843" data-mp="exj8p9kz6yun">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2844" data-mp="opypw4og7jeh">
          <ChevronLeft className="w-5 h-5" data-id="element-2845" data-mp="xr6nekj54g7i" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2846" data-mp="12p84x10x47gv">
          <ChevronRight className="w-5 h-5" data-id="element-2847" data-mp="34g1pk2prjevq" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-2848" data-mp="34iegh5di0zkx">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-2849" data-mp="2s6c2co9ixsjs" data-mpi={i} />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    addToCart,
    openCart
  } = useCart();
  // Lazy-init the default quantity per product. Soil/Mulch/Org. products
  // (topsoil, bark, compost, play chips, fill dirt) default to a 15-yd
  // light load — that's the typical homeowner spring-project size and
  // the size of one full light-material truckload. The 50/50 Mix is
  // the explicit exception (defaults to 12 — see getDefaultQuantity in
  // utils/loadSplit.ts). Everything else (rock, drain, quarry, decor)
  // defaults to a 12-yd heavy load.
  const [quantity, setQuantity] = useState<string>(() => {
    for (const cat of categories) {
      const found = cat.products.find(p => p.slug === slug);
      if (found) return getDefaultQuantity(found.name);
    }
    return '12';
  });
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  // Click-to-expand explainer for the Small-load rate label that now
  // sits ABOVE the big price number when the order is under the
  // small-load threshold. Replaces the verbose "surcharge applies"
  // subtext with a one-tap pulldown of plain-language pricing context.
  const [showSmallLoadInfo, setShowSmallLoadInfo] = useState(false);
  // Auto-dismiss the "Added to Truck!" confirmation card after 10s and
  // revert to the default Add-to-Truck state. Customer can still tap
  // Continue Shopping / View Cart at any point during the window.
  useEffect(() => {
    if (!addedFlash) return;
    const t = setTimeout(() => setAddedFlash(false), 10000);
    return () => clearTimeout(t);
  }, [addedFlash]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  // True when + has been pressed past 4 loads. Replaces the auto-fire
  // QuoteModal — the customer sees an amber "5+ loads" link below the
  // stepper and explicitly taps it to open the form.
  const [showQuoteWarning, setShowQuoteWarning] = useState(false);
  // Free-text yardage input — always active (no tap-to-edit handshake).
  // The readout *is* a transparent input styled to match the bold display.
  // Max 2 digits, clamped 1–96 on blur/Enter. The +/− pads keep this in
  // sync by writing to qtyDraft alongside quantity via the useEffect below.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Sync the input's displayed text whenever quantity changes externally
  // (e.g. via +/− pads, or estimatorQty handoff). Skip the sync if the
  // user is actively typing — we don't want to clobber their keystrokes.
  useEffect(() => {
    if (document.activeElement !== qtyInputRef.current) {
      setQtyDraft(String(Number(quantity) || 12));
    }
  }, [quantity]);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Custom yardage handed off from the Instant Estimator via ?qty=N (e.g. 17).
  // When set, the page bypasses the standard dropdown and adds-to-cart using
  // the exact estimator yardage via buildDeliveryLineItems, so the 5yd-per-
  // load minimum and the delivery split match the estimator's quote exactly.
  // Clearing it (via the "Change quantity" link) reverts to the dropdown.
  const [estimatorQty, setEstimatorQty] = useState<number | null>(null);
  // Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  const handleUseQuantity = (yards: number) => {
    const roundedYards = Math.ceil(yards);
    if (roundedYards === 0) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (roundedYards <= fullLoad) {
      // Single-load: pre-select the dropdown value at the actual yards the
      // customer asked for. Tiny 1–2 yd loads are accepted as-is and priced
      // with the micro-load formula by the cart's billing math.
      const snapped = Math.max(1, roundedYards);
      setQuantity(String(snapped));
      setNumLoads('');
      setIsCalcOpen(false);
    } else if (product && category) {
      // Multi-load: add directly to cart using the shared splitter so the
      // last partial load is billed at the 5yd minimum (e.g. 16yd → 12 + 5).
      // This avoids the old behavior of inflating qty to fullLoad × loads.
      const lineItems = buildDeliveryLineItems(roundedYards, product.name);
      lineItems.forEach(li => {
        addToCart({
          category: category.name,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: product.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      // Confirmation persists until the customer chooses Continue
      // Shopping or View Cart — gives them time to decide their next
      // step instead of the old 2s auto-dismiss.
      setAddedFlash(true);
      setIsCalcOpen(false);
    }
  };
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Pre-select quantity from estimator handoff (?qty=N). Runs after product
  // is resolved so we know whether it's light (15yd full load) or heavy (12yd).
  // - Single-load amounts (≤ fullLoad) snap to the dropdown directly.
  // - Multi-load amounts (e.g. 17yd) get stored as estimatorQty and the page
  //   adds-to-cart with the exact yardage on click, preserving the estimator's
  //   5yd-min split (12 + 5) rather than inflating to "2 full loads" (24).
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    if (!qtyParam) return;
    const n = parseInt(qtyParam, 10);
    // Accept any positive yardage — manual 1–2 yd entries flow through and
    // get priced with the micro-load formula (flat $250 delivery).
    if (isNaN(n) || n < 1) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (n <= fullLoad) {
      const snapped = Math.max(1, Math.min(fullLoad, n));
      setQuantity(String(snapped));
      setNumLoads('');
      setEstimatorQty(null);
    } else {
      // Keep the exact estimator yardage. Dropdown stays at its default;
      // the Add to Truck button will use estimatorQty via buildDeliveryLineItems.
      setEstimatorQty(n);
    }
  }, [slug, searchParams, product?.name]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-2850" data-mp="3or7dkmiss4lg">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2851" data-mp="3topm4bswkhsx">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-2852" data-mp="2jphfuf0dmio">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '5+') {
      setIsQuoteModalOpen(true);
      return;
    }
    // Estimator handoff takes priority — use the exact custom yardage so the
    // cart line items match the estimator's quote (e.g. 14 → 12 + 5, not 24).
    let finalQuantity: number;
    if (estimatorQty && estimatorQty > 0) {
      finalQuantity = estimatorQty;
    } else if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    } else {
      finalQuantity = Number(quantity);
    }
    // Split into per-delivery line items so the cart shows a clean breakdown
    // (Delivery 1 / Delivery 2 / ...) and the 5yd-per-load minimum is
    // enforced on any partial last load.
    const lineItems = buildDeliveryLineItems(finalQuantity, product.name);
    lineItems.forEach(li => {
      addToCart({
        category: category.name,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: product.pricePerYard,
        requestedQuantity: li.requestedQuantity
      });
    });
    // Confirmation persists until the customer chooses Continue
    // Shopping or View Cart.
    setAddedFlash(true);
  };
  // Multi-load custom-quote redirect REMOVED. The amber 5+ warning
  // inside the readout window is now the SOLE entry point to the
  // QuoteModal — no auto-fire when numLoads flips to '5+'. The user
  // explicitly has to tap the warning to open the contact form,
  // matching the Hero estimator's behavior.
  // Reset the quantity dropdown to the per-product default whenever the
  // user navigates to a different product. React Router reuses the same
  // <Product /> component instance when only the :slug param changes, so
  // the useState lazy initializer above only runs on FIRST mount. Without
  // this effect, picking Medium Bark after viewing 1.25" Minus would
  // leave the dropdown stuck at 12 yd instead of snapping to the 15 yd
  // light-load default.
  useEffect(() => {
    if (product?.name) {
      setQuantity(getDefaultQuantity(product.name));
      setNumLoads('');
    }
    // Intentionally only re-runs on slug change — manual dropdown
    // adjustments by the customer within the same product are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#f4f7f2] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2853" data-mp="h6t2aj8ncu9l">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2854" data-mp="14wneqzp20gtb">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-2855" data-mp="zzwzgel2pp8y">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-2856" data-mp="v7dlvhmdyu45">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2857" data-mp="q8itpp2j3t7d" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-2858" data-mp="1nprdgzh4pjus">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-[#fffff9] dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-2859" data-mp="1ix80o4q9msr1">
                <ChevronLeft className="w-4 h-4" data-id="element-2860" data-mp="208vdcv5i064d" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2861" data-mp="25657go3u15yj">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2862" data-mp="2a4pypy13wfau">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2863" data-mp="2e5rzijao8zex">
                <ChevronLeft className="w-4 h-4" data-id="element-2864" data-mp="2iybdvih6nyks" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-[#fffff9] dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-2865" data-mp="2nwmm5orty10w">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2866" data-mp="3j3oan0sgx88b">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2867" data-mp="3s5xfpb4yw9eb">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-2868" data-mp="3s5eamv0f1fhp" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2869" data-mp="3n6ulymuftpln">
                <ChevronRight className="w-4 h-4" data-id="element-2870" data-mp="2zijrq2j8yo6c" />
              </span>}
          </div>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(28rem,0.9fr)] md:items-start" data-id="element-2871" data-mp="2vgy87yc7jbn6">
          {/* Independent media surface — visually separate from the cart workspace. */}
          <div className="relative h-72 min-w-0 max-w-full overflow-hidden rounded-xl border border-[#e6ece3] bg-stone-200 shadow-sm sm:h-96 md:sticky md:top-6 md:h-[42rem] dark:border-[#262626] dark:bg-[#242424]" data-id="element-2872" data-mp="34ftf7cn2sprj">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-2873" data-mp="2brnrqi4n0mo0" />
          </div>

          {/* Independent details and ordering surface. */}
          <div className="min-w-0 max-w-full rounded-xl border border-[#e6ece3] bg-[#fffff9] p-6 shadow-sm sm:p-8 md:p-10 dark:border-[#363534] dark:bg-[#222222]" data-id="element-2874" data-mp="w1n5ipiocwy3">
            <div className="mb-2" data-id="element-2875" data-mp="n81sbam5rnqs">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-2876" data-mp="n9csz1kb4fdo">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2877" data-mp="1ez7mlaf76r85">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-2878" data-mp="15ysy626xsw0f">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2879" data-mp="160vx5bq2hzha">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2880" data-mp="9rusmsuu04ea" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2881" data-mp="3rwy02k89sshv">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2882" data-mp="3n07jzwwgtwwg" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2883" data-mp="3d8tgzf5b2eyk">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2884" data-mp="2km3lab5qhij1" />}
              </button>
            </div>

            <div className="mb-6" data-id="element-2885" data-mp="2fp3a5lu11pl2">
              <AnimatePresence mode="wait" data-id="element-2886" data-mp="2tmry52je5cuc">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2887" data-mp="2tkxctn47jipq">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-2888" data-mp="3dbiviypkr7dw">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-2889" data-mp="3mcgzxi3xdgx0">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-2890" data-mp="3hcm9ffo2atch">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2891" data-mp="2exd7z31pyaqo">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-2892" data-mp="2jvww9a3sbym7" data-mpi={idx}>
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="border border-[#d4ddcf] dark:border-emerald-500/20 rounded-sm p-4 flex items-start gap-3" data-id="element-2893" data-mp="32qvcsfbu4dnj">
                        <div className="bg-[#04A05C]/15 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-2894" data-mp="3bp76iq7zzrk7">
                          <Check className="w-4 h-4 text-[#04A05C] dark:text-[#22c95c]" data-id="element-2895" data-mp="1da45bfn8icdx" />
                        </div>
                        <div data-id="element-2896" data-mp="1hao1ogzzhbz0">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2897" data-mp="1hbxzr66u6k5n">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-2898" data-mp="150yayskjd7ib">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2899" data-mp="1sr11v5cps7fc">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2900" data-mp="1et42y2asfoxi">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-2901" data-mp="1i8vwfduzw90l">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-2902" data-mp="1m73bm49t929o">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-2903" data-mp="20ylz0n501z32">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-2904" data-mp="piduig60cdor">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-2905" data-mp="m3cammbs9yyz">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-2906" data-mp="3uhckvmzeys8k">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2907" data-mp="3gntpsaxgelq5">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-2908" data-mp="3bps4djck2abi">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-2909" data-mp="1fn6n2y8ute4y" />
                        </div>
                        <div className="min-w-0" data-id="element-2910" data-mp="1kkf5mzxwzk6q">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-2911" data-mp="1pczxhvx9fn7m">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-2912" data-mp="wokktg1lpgzg">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-2913" data-mp="15ohfpzel3c2y">
                        <Download className="w-4 h-4" data-id="element-2914" data-mp="1am93ch874c1c" />
                        <span className="hidden sm:inline" data-id="element-2915" data-mp="2h3e4d86uvz9s">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-2916" data-mp="2w64g6edvwl09">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-2917" data-mp="2mhmi44wzos0b">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-2918" data-mp="2igu8hak7gl3i" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2919" data-mp="2dhr3tr850gw2">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-2920" data-mp="28hv1rxj02zj5">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-2921" data-mp="23kurfaf4bam8">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@206gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-2922" data-mp="3tvha94mqbh8v">
                            info@206gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div data-id="element-2923" data-mp="c5qosef2xwao">
              {isSpecialty ? <div className="space-y-4" data-id="element-2924" data-mp="3tfgru96hmm7o">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-2925" data-mp="3tef5v9nxofb6">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-2926" data-mp="3kfa7q4gaanzj">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2927" data-mp="1iluer0wq4x1e">
                    <Phone className="w-5 h-5 mr-2" data-id="element-2928" data-mp="1st5dhhdkjqwi" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-4" data-id="element-2929" data-mp="26kjxnrj7py55">
                  {(() => {
                const fullLoad = isLight ? 15 : 12;
                const smallLoadThreshold = 10;
                const requestedYards = estimatorQty ? estimatorQty : quantity === 'multi' && numLoads && numLoads !== '5+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                const loads: number[] = [];
                if (requestedYards > 0) {
                  if (requestedYards <= fullLoad) {
                    loads.push(requestedYards);
                  } else {
                    const count = Math.ceil(requestedYards / fullLoad);
                    const last = requestedYards - (count - 1) * fullLoad;
                    for (let i = 0; i < count - 1; i++) loads.push(fullLoad);
                    loads.push(last);
                  }
                }
                const billableYards = loads.reduce((a, b) => a + b, 0);
                const bulkPct = billableYards >= fullLoad * 4 ? 0.1 : billableYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                // Build per-delivery line totals so the multi-load
                // breakdown can mirror the Services hub: each
                // delivery shows its yardage and its post-discount
                // dollar amount on its own row. preDiscountTotal is
                // the strikethrough "Was $X" anchor for the bulk
                // discount headline.
                const deliveryLoads: {
                  yards: number;
                  total: number;
                }[] = [];
                let hasSurchargedLoad = false;
                for (const ld of loads) {
                  let sur = 1;
                  if (ld >= 3 && ld < smallLoadThreshold) {
                    sur = 1 + (smallLoadThreshold - ld) * 0.1;
                  }
                  if (sur > 1) hasSurchargedLoad = true;
                  const loadTotal = Math.ceil(product.pricePerYard * sur) * ld;
                  deliveryLoads.push({
                    yards: ld,
                    total: loadTotal
                  });
                }
                const preDiscountTotal = deliveryLoads.reduce((s, l) => s + l.total, 0);
                let totalCost = preDiscountTotal;
                if (bulkPct > 0) {
                  totalCost = Math.round(totalCost * (1 - bulkPct));
                  // Apply the same bulk discount to each per-delivery
                  // total so the rows sum to the discounted grand
                  // total (with at most a rounding-cent of drift).
                  deliveryLoads.forEach((l, i) => {
                    deliveryLoads[i] = {
                      ...l,
                      total: Math.round(l.total * (1 - bulkPct))
                    };
                  });
                }
                const effectiveAvg = billableYards > 0 ? Math.round(totalCost / billableYards) : product.pricePerYard;
                const showsEffective = billableYards > 0 && (hasSurchargedLoad || bulkPct > 0) && effectiveAvg !== product.pricePerYard;
                // Past 4 full loads the order rolls into custom-quote
                // territory. The bulk-discount formula no longer
                // reflects what dispatch will actually charge (hand
                // loads, mixed truck sizes, etc.), so we suppress
                // the savings copy past that threshold rather than
                // surface a stale number.
                const isCustomQuoteZone = deliveryLoads.length >= 5;
                return <div className="border-b border-stone-100 dark:border-[#444444] pb-4" data-id="element-2930" data-mp="kie6ceu47a3n">
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3" data-id="element-2931" data-mp="fkcqs3vwdswx">
                          {/* Left: price column. The label above the
                           big number is state-aware:
                           - Bulk discount tier reached: amber pill
                           "5% Bulk Discount" (uppercase, prominent).
                           - Under the small-load threshold: clickable
                           "Small-load rate ⓘ" — tapping toggles a
                           full-width pulldown explainer below the
                           two-column header.
                           - Otherwise: the default "Price" label. */}
                          <div className="min-w-0 flex-1" data-id="element-2932" data-mp="30wztuprq0plp">
                            {/* State label — all three variants render
                             at the same H-6 height so the big "$XX
                             /yard" number stays on the same baseline
                             as the right-column total regardless of
                             which label is showing.
                             Priority order:
                             1. Bulk discount (green pill) — customer
                             paying LESS.
                             2. Small-load surcharge (amber pill) —
                             customer paying MORE. Triggers off
                             hasSurchargedLoad alone so the pill
                             appears even on small-load orders where
                             the rounded effective rate happens to
                             match the base rate.
                             3. Default "Price" label otherwise. */}
                            <div className="h-6 mb-1 flex items-center" data-id="element-2933" data-mp="2ir8rohic1bow">
                              {!isCustomQuoteZone && bulkPct > 0 ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#04A05C] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-2934" data-mp="2epxa8z9m3wrs">
                                  {Math.round(bulkPct * 100)}% Bulk Discount
                                </span> : !isCustomQuoteZone && hasSurchargedLoad ? <button type="button" onClick={() => setShowSmallLoadInfo(value => !value)} aria-expanded={showSmallLoadInfo} aria-controls="small-load-rate-explainer" className="inline-flex min-h-7 items-center gap-1 rounded-md bg-[#CB6E0A] px-2 py-1 text-[10px] font-bold uppercase leading-none tracking-wide text-white transition-colors hover:bg-[#9a5608] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CB6E0A] focus-visible:ring-offset-2 dark:bg-[#F6A93B] dark:text-stone-900 dark:hover:bg-[#ffc86a] dark:focus-visible:ring-[#F6A93B] dark:focus-visible:ring-offset-[#2a2a2a]" data-id="element-2935" data-mp="3eydhrqdnuj1k">
                                  Small-load rate <Info className="h-3 w-3" aria-hidden="true" data-id="element-2936" data-mp="akx67xq951g8" />
                                </button> : <p className="text-sm text-brand-muted dark:text-[#22c95c] font-medium" data-id="element-2937" data-mp="fhnngodsm338">
                                  Price
                                </p>}
                            </div>
                            {isCustomQuoteZone ? <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-2938" data-mp="2hc64ls3ajidl">
                                Custom quote
                              </p> : <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2939" data-mp="3fn7m0ku4f97i">
                                $
                                {showsEffective ? effectiveAvg : product.pricePerYard}{' '}
                                {/* Secondary "/ yard" label uses the
                                 same neutral muted-grey as the right
                                 column's "{N} yds /" and "+ delivery"
                                 so the metadata reads uniformly
                                 regardless of small-load / bulk
                                 discount state. "avg" dropped per
                                 design — the headline rate IS the
                                 customer's per-yard cost. */}
                                <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-2940" data-mp="l6nbydy71dh8">
                                  / yard
                                </span>
                              </p>}
                            {/* Subtext under the big number. For a small
                             load: single line with strikethrough base
                             rate + plain "base rate at N yd+" copy
                             (the clickable label above carries the
                             surcharge messaging now). For a bulk
                             discount: single line with strikethrough
                             base rate + "Save $X on N yards". */}
                            {showsEffective && hasSurchargedLoad && !bulkPct && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 whitespace-nowrap" data-id="element-2941" data-mp="c15izrpuc3th">
                                  ${product.pricePerYard}/yd at{' '}
                                  {smallLoadThreshold} yd+
                                </p>}
                            {showsEffective && bulkPct > 0 && !isCustomQuoteZone && (
                      /* Savings copy under the price reads
                      accent green (matches the green pill
                      above) — the customer is paying LESS
                      here, so the color reinforces that
                      even when the eye skips the label. */
                      <p className="text-sm mt-1" data-id="element-2942" data-mp="8l41uz829onv">
                                  <span className="line-through text-stone-500 dark:text-stone-400" data-id="element-2943" data-mp="4kab7y38hvra">
                                    ${product.pricePerYard}/yd
                                  </span>
                                  <span className="text-[#04A05C] ml-2 font-semibold" data-id="element-2944" data-mp="3qzv9mor7d7rm">
                                    $
                                    {product.pricePerYard * billableYards - totalCost}{' '}
                                    savings
                                  </span>
                                </p>)}
                            {!showsEffective && billableYards === 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5" data-id="element-2945" data-mp="3d2q02esa5qrm">
                                Base price at {smallLoadThreshold}+ yards per
                                delivery. Smaller loads include a surcharge.
                              </p>}
                          </div>
                          {/* Right: materials subtotal — mirrors the
                           "$87 / yard" pattern on the left by putting the
                           small qualifier ("12 yds /") IN FRONT of the
                           big number ("$1,044"). The leading-blank top
                           row keeps the big numbers baseline-aligned
                           with the Price column even though the right
                           column has no top label. */}
                          {billableYards > 0 && <div className="min-w-0 text-right" data-id="element-2946" data-mp="q4ch2hllqzm3">
                              <div aria-hidden="true" className="mb-1 h-6" data-id="element-2947" data-mp="q2028db555d9" />
                              {isCustomQuoteZone ? <>
                                  <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-2948" data-mp="379rtt0cqq9hk">
                                    <span className="text-sm font-normal text-brand-muted dark:text-[#22c95c]" data-id="element-2949" data-mp="3c94hfnbfrkl7">
                                      {billableYards} yds /
                                    </span>{' '}
                                    Custom
                                  </p>
                                  <p className="text-xs text-brand-orange mt-0.5" data-id="element-2950" data-mp="3pyy7ua2lacx1">
                                    text us for pricing
                                  </p>
                                </> : <>
                                  {/* Secondary metadata ("{N} yds /"
                                   and "+ delivery") uses the same
                                   neutral muted-grey as the left
                                   column's "/ yard" so the surrounding
                                   metadata reads uniformly white →
                                   grey regardless of discount state.
                                   Only the BIG numbers stay white. */}
                                  <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-2951" data-mp="mwcxdrghwv63">
                                    <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-2952" data-mp="it6osd5wurwg">
                                      {billableYards} yds /
                                    </span>{' '}
                                    ${totalCost.toLocaleString()}
                                  </p>
                                  {/* "+ delivery" mirrors the green
                                   "Price" label on the left so the
                                   two columns book-end the row in
                                   matching accent colors. */}
                                  <p className="text-xs text-brand-muted dark:text-[#22c95c] mt-0.5" data-id="element-2953" data-mp="3ma6vpao2swim">
                                    + delivery
                                  </p>
                                </>}
                            </div>}
                        </div>

                        {/* Section-wide explainer pulldown for the
                         Small-load rate label. Spans the entire price
                         card so it has room to breathe (rather than
                         squeezing into the left column under the
                         numbers). */}
                        <AnimatePresence data-id="element-2954" data-mp="301ro9wsgxdn9">
                          {showSmallLoadInfo && !isCustomQuoteZone && showsEffective && hasSurchargedLoad && !bulkPct && <motion.div id="small-load-rate-explainer" initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-2955" data-mp="2r3pp7msaov2m">
                                <p className="mt-3 p-3 rounded-lg bg-[#CB6E0A]/10 dark:bg-[#F6A93B]/10 border border-[#CB6E0A]/30 dark:border-[#F6A93B]/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed" data-id="element-2956" data-mp="1xj83yb1wkx9l">
                                  A small load runs the truck below capacity.
                                  Fixed loading costs (loader operator, scale,
                                  ticketing) get split across fewer yards, so
                                  the per-yard rate goes up. Order{' '}
                                  {smallLoadThreshold}+ yd in a single delivery
                                  to drop to the base ${product.pricePerYard}/yd
                                  rate.
                                </p>
                              </motion.div>}
                        </AnimatePresence>

                      </div>;
              })()}

                  {/* Estimator handoff banner — appears when the user
                   arrived from the Instant Estimator with a custom yardage
                   that needs to split across multiple deliveries. Without
                   this chip, the dropdown still shows its default ("12 Yards")
                   and the user thinks the page ignored their calculated
                   amount. Clicking "Change quantity" reverts to the dropdown. */}
                  {estimatorQty && estimatorQty > 0 && (() => {
                const loads = (() => {
                  const fullLoad = isLight ? 15 : 12;
                  const MIN = 5;
                  const n = estimatorQty;
                  if (n <= fullLoad) return [Math.max(MIN, n)];
                  const count = Math.ceil(n / fullLoad);
                  const last = n - (count - 1) * fullLoad;
                  const billableLast = Math.max(MIN, last);
                  const arr: number[] = [];
                  for (let i = 0; i < count - 1; i++) arr.push(fullLoad);
                  arr.push(billableLast);
                  return arr;
                })();
                const breakdown = loads.join(' + ');
                const totalBillable = loads.reduce((a, b) => a + b, 0);
                return <motion.div initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="rounded-xl border border-[#04A05C]/30 bg-[#04A05C]/10 p-3 sm:p-4" data-id="element-2957" data-mp="3fnmjgsjapuqm">
                          <div className="flex items-start gap-3" data-id="element-2958" data-mp="30qvg686i3yvq">
                            <div className="w-8 h-8 rounded-full bg-[#04A05C]/20 flex items-center justify-center shrink-0" data-id="element-2959" data-mp="2wq1tfi4soq4f">
                              <Check className="w-4 h-4 text-[#04A05C]" data-id="element-2960" data-mp="2rsa32sjstc2c" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2961" data-mp="uxx32b2y20vt">
                              <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-2962" data-mp="q052kso8h6g0">
                                From your estimator: {estimatorQty}{' '}
                                {estimatorQty === 1 ? 'yard' : 'yards'}
                              </p>
                              <p className="text-xs text-brand-charcoal/70 dark:text-stone-300 mt-0.5" data-id="element-2963" data-mp="1dx1pqz1nd7qj">
                                Splits into {loads.length}{' '}
                                {loads.length === 1 ? 'delivery' : 'deliveries'}{' '}
                                ({breakdown} yd)
                                {totalBillable !== estimatorQty && <span className="text-amber-700 dark:text-amber-400" data-id="element-2964" data-mp="1wuco8hfgfx4x">
                                    {' '}
                                    · billed as {totalBillable} yd (3 yd order
                                    min)
                                  </span>}
                              </p>
                            </div>
                            <button type="button" onClick={() => setEstimatorQty(null)} className="shrink-0 text-xs font-semibold text-brand-charcoal/70 dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange underline underline-offset-2" data-id="element-2965" data-mp="7h7ps051se5i">
                              Change
                            </button>
                          </div>
                        </motion.div>;
              })()}

                  <div className="space-y-2" data-id="element-2966" data-mp="8waib52uyzo7">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-2967" data-mp="8xllp3se65es">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-2968" data-mp="3hd5b1rpxz3ok">
                        {estimatorQty ? 'Or change quantity' : 'Select Quantity'}
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-2969" data-mp="2yjj9gcgp8o67">
                            5% at {tier2Yards} yd · 10% at {tier3Yards} yd
                          </span>;
                  })()}
                    </div>
                    {/* Floating stepper — no container background, just
                     big bold + and − pads flanking the live readout.
                     active:* gives instant green flash on press. Below
                     a full load increments by 1 yard; at/above a full
                     load snaps to the next full-load multiple (2, 3,
                     4 loads). + at 4 loads flips the row below into an
                     amber "Need 5+ loads?" link (does NOT auto-open the
                     custom-quote modal — the customer explicitly taps
                     the link). */}
                    <div className="flex flex-col gap-3" data-id="element-2970" data-mp="35r87fji34qlr">
                      <div className="flex min-h-[68px] items-stretch gap-3 sm:min-h-[79px]" data-id="element-2971" data-mp="35st0t5sny3i1">
                        <div className="flex min-w-0 flex-1 items-center rounded-lg border border-stone-300 bg-transparent px-5 text-2xl font-extrabold leading-none tabular-nums text-brand-charcoal dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white sm:px-6 sm:text-3xl" data-id="element-2972" data-mp="3ao8bjovdjja8">
                          {(() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        const loadCount = current > fullLoad ? Math.round(current / fullLoad) : 1;
                        const isFullLoad = current === fullLoad;
                        const isMin = current === 3;
                        const commit = () => {
                          const parsed = parseInt(qtyDraft, 10);
                          if (isNaN(parsed) || parsed <= 0) {
                            // Empty / invalid → revert input text to current.
                            setQtyDraft(String(current));
                            return;
                          }
                          // Cap at 8 loads (96 yd heavy / 120 yd light).
                          const max = fullLoad * 8;
                          const clamped = Math.min(max, Math.max(1, parsed));
                          setQuantity(String(clamped));
                          setQtyDraft(String(clamped));
                          // Flip the amber "text us" CTA once past 4 loads.
                          if (clamped > fullLoad * 4) {
                            setShowQuoteWarning(true);
                          } else {
                            setShowQuoteWarning(false);
                          }
                        };
                        return <>
                                <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                            // Numeric only, max 2 digits.
                            const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                            setQtyDraft(v);
                          }} onBlur={commit} onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.currentTarget as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setQtyDraft(String(current));
                              (e.currentTarget as HTMLInputElement).blur();
                            }
                          }} aria-label="Quantity in yards" style={{
                            width: `${Math.max(1, qtyDraft.length)}ch`
                          }} className={`bg-transparent border-0 outline-none text-center text-2xl font-bold tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-2973" data-mp="3v5csm3okb1ii" />
                                <span className={`hidden sm:inline transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-2974" data-mp="1rn1uv4pcdlkz">
                                  {' '}
                                  yards
                                </span>
                                <span className={`sm:hidden transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-2975" data-mp="1dvnd11767qfi">
                                  {' '}
                                  yd
                                </span>
                                {(loadCount > 1 || loadCount === 1 && Number(quantity) === (isLight ? 15 : 12)) && <span className="ml-2 text-xs sm:text-sm font-medium text-brand-muted dark:text-stone-400 align-baseline whitespace-nowrap" data-id="element-2976" data-mp="21mj1ppgby5ij">
                                    ({loadCount}{' '}
                                    {loadCount === 1 ? 'load' : 'loads'})
                                  </span>}
                              </>;
                      })()}
                        </div>
                        <div className="flex shrink-0 gap-3" data-id="element-2977" data-mp="31swb1992nsci">
                          <button type="button" onClick={() => {
                        const fullLoad = isLight ? 15 : 12;
                        const MIN = 3;
                        const current = Number(quantity) || 12;
                        if (current <= fullLoad) {
                          if (current > MIN) setQuantity(String(current - 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          const newLoads = currentLoads - 1;
                          setQuantity(String(newLoads * fullLoad));
                          // Clear the amber CTA once back at 4 loads or less.
                          if (newLoads <= 4) setShowQuoteWarning(false);
                        }
                      }} aria-label="Decrease quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="group flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-transparent text-brand-charcoal transition-colors duration-150 hover:bg-[#059F40]/10 active:bg-[#059F40]/20 active:text-[#059F40] dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white dark:hover:bg-[#333333] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c] sm:h-[72px] sm:w-[72px]" data-id="element-2978" data-mp="31t4n4yxdetmn">
                            <Minus className="h-8 w-8 transition-transform duration-150 ease-out group-active:scale-125 sm:h-9 sm:w-9" strokeWidth={2.5} data-id="element-2979" data-mp="g6ac0j372dr2" />
                          </button>
                          <button type="button" onClick={() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        if (current < fullLoad) {
                          setQuantity(String(current + 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          // Cap at 8 loads. Past 4 loads the customer
                          // is in "large order" territory and the
                          // primary CTA flips to the amber "text us"
                          // button — but the stepper still works so
                          // they can show us the exact yardage they
                          // need before tapping through to SMS.
                          if (currentLoads >= 8) return;
                          const newLoads = currentLoads + 1;
                          setQuantity(String(newLoads * fullLoad));
                          if (newLoads >= 5) setShowQuoteWarning(true);
                        }
                      }} aria-label="Increase quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="group flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-transparent text-brand-charcoal transition-colors duration-150 hover:bg-[#059F40]/10 active:bg-[#059F40]/20 active:text-[#059F40] dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white dark:hover:bg-[#333333] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c] sm:h-[72px] sm:w-[72px]" data-id="element-2980" data-mp="uwzpxt81i6ym">
                            <Plus className="h-8 w-8 transition-transform duration-150 ease-out group-active:scale-125 sm:h-9 sm:w-9" strokeWidth={2.5} data-id="element-2981" data-mp="309ux9a4l5hnv" />
                          </button>
                        </div>
                      </div>

                      {/* Large-order handoff — when + has pushed past
                       4 loads, the primary CTA below morphs into an
                       amber "Text us for a quote" button instead of a
                       separate amber pulldown. */}

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="space-y-3" data-id="element-2982" data-mp="jl7jljhd0hzs">
                          <div className="w-full flex items-center justify-center bg-[#04A05C]/10 dark:bg-[#006241]/40 text-[#04A05C] dark:text-[#22c95c] rounded-none font-bold border border-[#04A05C] dark:border-[#22c95c] py-4 text-lg" data-id="element-2983" data-mp="22gvbu8zfy1vo">
                            <Check className="w-5 h-5 mr-2" data-id="element-2984" data-mp="2b85ecpsfx8ci" />
                            Added to Truck!
                          </div>
                          <div className="flex gap-2" data-id="element-2985" data-mp="1elivua3uvl7x">
                            <button onClick={() => {
                        setAddedFlash(false);
                        navigate('/#materials');
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-2986" data-mp="1jj27r3d2zlwp">
                              Continue Shopping
                            </button>
                            <button onClick={() => {
                        setAddedFlash(false);
                        openCart();
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-2987" data-mp="2fkr57o4l5l90">
                              <ShoppingCart className="w-4 h-4" data-id="element-2988" data-mp="2jlktiilxx22f" />
                              View Cart
                            </button>
                          </div>
                        </motion.div> : showQuoteWarning ? <button onClick={() => {
                    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                    if (!isMobile) {
                      setIsQuoteModalOpen(true);
                      return;
                    }
                    const fullLoad = isLight ? 15 : 12;
                    const yds = Number(quantity) || fullLoad * 5;
                    const loads = Math.max(5, Math.round(yds / fullLoad));
                    const body = `Hi 206Gravel — I need a custom quote for ${loads} loads of ${product.name} (${yds} yd). Thanks!`;
                    window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                  }} className="w-full py-[18px] rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center bg-[#DD4132]/15 dark:bg-[#DD4132]/15 hover:bg-[#DD4132]/25 dark:hover:bg-[#DD4132]/25 text-[#DD4132] dark:text-[#DD4132] border-2 border-[#DD4132]/40 hover:border-[#DD4132]/60" aria-live="polite" data-id="element-2989" data-mp="2tcp6xdvywev5">
                          <MessageSquare className="w-5 h-5 mr-2" data-id="element-2990" data-mp="65t0tb8pkupx" />
                          Large order — text us →
                        </button> : (() => {
                    const current = Number(quantity) || 12;
                    const fullLoad = isLight ? 15 : 12;
                    const loadPct = Math.min(100, Math.max(0, current / fullLoad * 100));
                    return <button onClick={handleAddToCart} className="relative w-full overflow-hidden bg-[#04702E] text-white py-[21px] rounded-none font-bold text-lg flex items-center justify-center shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-2991" data-mp="3919z3zl0pfjb">
                              <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 bg-[#0DA24B]" initial={false} animate={{
                        width: `${loadPct}%`
                      }} transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 26
                      }} data-id="element-2992" data-mp="2z5f92jegsi6i" />
                              <span className="relative z-10 flex items-center justify-center" data-id="element-2993" data-mp="3iqjo36kkei37">
                                <Truck className="w-5 h-5 mr-2" data-id="element-2994" data-mp="3msex6x8qlzrj" />
                                Add to Truck
                              </span>
                            </button>;
                  })()}
                    </div>

                    <div className="flex justify-center" data-id="element-2995" data-mp="2bfktvv6oahj3">
                      <SameDayBadge variant="full" data-id="element-2996" data-mp="23tz4vvfvskk5" />
                    </div>

                    {/* Material sanity check — helps the customer confirm
                     the product they're viewing matches their project
                     BEFORE adding to cart. Lives here (not in the cart
                     drawer) so the decision happens with intent fresh.
                     The helper is now category-aware: each category in
                     data/products.ts ships its own pair of questions
                     and its own recommendation table inside
                     components/CartHelper.tsx, so the suggestions stay
                     relevant whether the customer is browsing base rock,
                     clear & drain rock, soil/mulch, quarry rip-rap, or
                     decorative landscape stone. */}
                    <CartHelper contextProduct={{
                  slug: product.slug,
                  name: product.name
                }} data-id="element-2997" data-mp="12bopee6kozzi" />

                    {/* Multi-load delivery-fee notice — visible whenever
                     the stepper is open. The "please select how many"
                     prompt is no longer needed because the stepper
                     auto-seeds 2 loads on entering multi mode. */}
                    <AnimatePresence data-id="element-2998" data-mp="20tz1mbqj0m0q">
                      {quantity === 'multi' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-2999" data-mp="20tgz4xd9tbkd">
                          <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-3000" data-mp="1i37n6vrom5fh">
                            <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-3001" data-mp="2oirqjo8qdtlu" />
                            <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-3002" data-mp="2thl6rofejypp">
                              Multi-load orders require separate deliveries. You
                              will be charged a delivery fee per load.
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                </div>}
            </div>
          </div>
        </div>

        <section className="mt-10" aria-labelledby="product-support-heading" data-id="element-3003" data-mp="3v9xi9210hxoa">
          <div className="mb-5" data-id="element-3004" data-mp="3v7ui3p65ao5u">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#05894e] dark:text-[#4ed491]" data-id="element-3005" data-mp="3qaule4v6qxre">Plan with confidence</p>
            <h2 id="product-support-heading" className="mt-1 font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-3006" data-mp="3lcj8tmdia95h">Resources for your project</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3007" data-mp="1jkwh1ilsia7y">Get the quantity right, prepare your delivery space, and use practical project guidance before your material arrives.</p>
          </div>

          <div className={`grid gap-4 md:grid-cols-3 ${isCalcOpen ? 'md:items-start' : 'md:items-stretch'}`} data-id="element-3008" data-mp="1mn0xvjvk82b6">
            <div className="space-y-4 md:flex md:h-full md:flex-col" data-id="element-3009" data-mp="1rj9j9f0corqs">
              <button type="button" onClick={() => setIsCalcOpen(value => !value)} aria-expanded={isCalcOpen} className="w-full rounded-xl border border-[#05894e]/35 bg-[#05894e]/5 p-5 text-left transition-colors hover:bg-[#05894e]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:bg-[#05894e]/10 md:flex-1" data-id="element-3010" data-mp="1cna3rjawvpbm">
                <CalcIcon className="h-6 w-6 text-[#04A05C] dark:text-[#04A05C]" aria-hidden="true" data-id="element-3011" data-mp="18ob2q9sawcpg" />
                <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3012" data-mp="tvr15dksuda4">Calculate how much you need</h3>
                <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3013" data-mp="kx5mhtig7iem">Estimate yards from your project dimensions and apply the result directly to this order.</p>
                <span className="mt-4 inline-flex text-sm font-bold text-[#05894e] dark:text-[#04A05C] md:mt-auto md:pt-4" data-id="element-3014" data-mp="6a6xe572d032">{isCalcOpen ? 'Hide calculator' : 'Open calculator'}</span>
              </button>
              <AnimatePresence data-id="element-3015" data-mp="fav8os52omc9">
                {isCalcOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.22
              }} className="overflow-hidden" data-id="element-3016" data-mp="fbo9lchcohmh">
                    <div className="rounded-xl border border-[#05894e]/25 bg-[#fffff9] p-5 dark:bg-[#222222]" data-id="element-3017" data-mp="y1wbjr9bd213">
                      <SimpleAreaCalculator materialSlug={product.slug} onApply={handleUseQuantity} data-id="element-3018" data-mp="u0u78w0jhzs9" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <Link to="/delivery-prep" className="flex flex-col rounded-xl border border-stone-200 bg-[#fffff9] p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:bg-[#222222] dark:hover:bg-[#303030] md:h-full" data-id="element-3019" data-mp="131ikvpm0ty4m">
              <Truck className="h-6 w-6 text-[#04A05C] dark:text-[#04A05C]" aria-hidden="true" data-id="element-3020" data-mp="9m6jeariq0t6" />
              <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3021" data-mp="eg0w2u8phfir">Delivery prep guide</h3>
              <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3022" data-mp="jd9hr4t2c1or">Check access, placement, and site conditions so delivery day goes smoothly.</p>
              <span className="mt-4 inline-flex text-sm font-bold text-[#04A05C] dark:text-[#04A05C] md:mt-auto md:pt-4" data-id="element-3023" data-mp="sfafs3w4errh">Prepare your site <ArrowRight className="ml-1 h-4 w-4" data-id="element-3024" data-mp="xcixy4h9qvsq" /></span>
            </Link>
            <Link to="/blog" className="flex flex-col rounded-xl border border-stone-200 bg-[#fffff9] p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:bg-[#222222] dark:hover:bg-[#303030] md:h-full" data-id="element-3025" data-mp="2u6negb7hw3f3">
              <FileText className="h-6 w-6 text-[#04A05C] dark:text-[#04A05C]" aria-hidden="true" data-id="element-3026" data-mp="2msuymdkjkd3s" />
              <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3027" data-mp="2hvw5nycm6c76">DIY project guides</h3>
              <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3028" data-mp="2cxuojiyr0myc">Learn the right material depth, base layers, and installation approach for common projects.</p>
              <span className="mt-4 inline-flex text-sm font-bold text-[#04A05C] dark:text-[#04A05C] md:mt-auto md:pt-4" data-id="element-3029" data-mp="35m8lrwi0jf8u">Explore guides <ArrowRight className="ml-1 h-4 w-4" data-id="element-3030" data-mp="30k17v1dmhp3v" /></span>
            </Link>
          </div>
        </section>

        <ProductComparisonPanel product={product} categoryName={category.name} data-id="element-3031" data-mp="2wmljl8hgm5eq" />
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-3032" data-mp="1p4kvtwcxllbm" />

      {/* Old fixed-position Calculator Drawer removed — replaced by the
                                                                                                                                                                                                                                                                          inline "Calculate how much I need" pulldown under the Add to Truck
                                                                                                                                                                                                                                                                          button, matching the Services hub product detail layout. */}
    </motion.main>;
}